import React, { useState, useEffect } from "react";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";

// Worker URL - update after deployment
const VOICE_WORKER_URL =
  process.env.REACT_APP_VOICE_WORKER_URL ||
  "https://voice-processor.ryan-b00.workers.dev";

/**
 * VoiceButton - Universal voice recording button with processing
 *
 * Props:
 * - contextType: Type of voice note (shift_note, photo_description, etc.)
 * - jobId: Associated job ID
 * - shiftId: Associated shift ID (optional)
 * - screenContext: Additional context about where button is used
 * - onResult: Callback with processed result
 * - onError: Callback for errors
 * - onRecordingChange: Callback when recording state changes (isRecording: boolean)
 * - disabled: Disable the button
 * - compact: Use smaller button style for inline placement
 * - style: Additional styles
 */
export default function VoiceButton({
  contextType = "shift_note",
  jobId,
  shiftId,
  screenContext,
  onResult,
  onError,
  onRecordingChange,
  disabled = false,
  compact = false,
  style = {},
}) {
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState("idle"); // idle, recording, uploading, processing

  const {
    isRecording,
    duration,
    error: recorderError,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder({
    maxDuration: 120000, // 2 minutes max
    onComplete: async (blob) => {
      await processAudio(blob);
    },
  });

  // Notify parent when recording state changes
  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  const processAudio = async (blob) => {
    try {
      setProcessing(true);
      setStatus("uploading");

      // 1) Upload to R2 via Worker
      const formData = new FormData();
      formData.append("audio", blob, "recording.m4a");

      const uploadRes = await fetch(`${VOICE_WORKER_URL}/api/voice/upload`, {
        method: "POST",
        body: formData,
      });

      if (!uploadRes.ok) {
        const errorText = await uploadRes.text();
        throw new Error(`Upload failed: ${errorText}`);
      }

      const { r2Key } = await uploadRes.json();

      // 2) Process via Worker
      setStatus("processing");

      const processRes = await fetch(`${VOICE_WORKER_URL}/api/voice/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          r2Key,
          context_type: contextType,
          job_id: jobId,
          shift_id: shiftId,
          screen_context: screenContext,
        }),
      });

      if (!processRes.ok) {
        const errorText = await processRes.text();
        throw new Error(`Processing failed: ${errorText}`);
      }

      const result = await processRes.json();

      onResult?.(result);
      setStatus("idle");
    } catch (err) {
      console.error("Voice processing error:", err);
      onError?.(err);
      setStatus("idle");
    } finally {
      setProcessing(false);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    if (isRecording) {
      stopRecording();
    } else if (!processing) {
      startRecording().catch((err) => {
        onError?.(err);
      });
      setStatus("recording");
    }
  };

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const getButtonText = () => {
    if (recorderError) return compact ? "Error" : recorderError;
    if (status === "recording") {
      return compact
        ? formatDuration(duration)
        : `${formatDuration(duration)} - Tap to Stop`;
    }
    if (status === "uploading")
      return compact ? "Uploading..." : "Uploading...";
    if (status === "processing")
      return compact ? "Processing..." : "Processing...";
    return compact ? "Voice" : "Voice Note";
  };

  const getButtonStyle = () => {
    // Compact style for inline placement (like next to labels) - but still tappable
    if (compact) {
      const compactBase = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        borderRadius: "10px",
        padding: "10px 16px",
        fontSize: "14px",
        fontWeight: "600",
        border: "none",
        cursor: disabled
          ? "not-allowed"
          : processing && !isRecording
            ? "wait"
            : "pointer",
        transition: "all 0.2s",
        opacity: disabled ? 0.5 : 1,
        ...style,
      };

      if (isRecording) {
        return {
          ...compactBase,
          background: "#fee2e2",
          color: "#dc2626",
          border: "1px solid #dc2626",
        };
      }

      if (processing) {
        return {
          ...compactBase,
          background: "#eff6ff",
          color: "#3b82f6",
          border: "1px solid #3b82f6",
        };
      }

      return {
        ...compactBase,
        background: "#f1f5f9",
        color: "#475569",
        border: "1px solid #cbd5e1",
      };
    }

    // Full-size style
    const baseStyle = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      borderRadius: "12px",
      padding: "14px 20px",
      fontSize: "16px",
      fontWeight: "600",
      minWidth: "140px",
      border: "none",
      cursor: disabled
        ? "not-allowed"
        : processing && !isRecording
          ? "wait"
          : "pointer",
      transition: "all 0.2s",
      opacity: disabled ? 0.5 : 1,
      ...style,
    };

    if (isRecording) {
      return {
        ...baseStyle,
        background: "#fee2e2",
        color: "#ef4444",
        border: "2px solid #ef4444",
        animation: "pulse 1.5s infinite",
      };
    }

    if (processing) {
      return {
        ...baseStyle,
        background: "#eff6ff",
        color: "#3b82f6",
        border: "2px solid #3b82f6",
      };
    }

    return {
      ...baseStyle,
      background: "#1a5a8e",
      color: "white",
      border: "2px solid #1a5a8e",
    };
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled || (processing && !isRecording)}
      style={getButtonStyle()}
    >
      <span style={{ fontSize: compact ? "16px" : "20px" }}>
        {isRecording ? "🔴" : processing ? "⏳" : "🎤"}
      </span>
      {getButtonText() && <span>{getButtonText()}</span>}
    </button>
  );
}
